import React, { useEffect, useState } from "react";
import { Settings2, Trash2, Plus, CheckCircle, XCircle, CircleDot, ChevronDown } from "lucide-react";
import ConfirmationModal from "../../components/confirmationModal";
import { automationActionData } from "../../utils/globalConstants";
import ActionSelectionPopup from "./ActionSelectionPopup"; // NEW: Import the popup component

interface ActionData {
  type: string;
  data: any;
}

interface StatusData {
  value: string;
  name: string;
}

interface UserData {
  _id: string;
  firstName: string;
}

interface LabelData {
  value: string;
  name: string;
}

interface LeadListData {
  _id: string;
  name: string;
}

interface TemplateData {
  _id: string;
  name: string;
}

interface TaskData {
  value: string;
  name: string;
}

interface DayMetrics {
  completedActionsCount: number;
  failedActionsCount: number;
  totalActions: number;
}

interface DayGroupProps {
  dayKey: string;
  actions: Array<ActionData>;
  onAddAction: (dayKey: string) => void;
  onActionChange: (dayKey: string, actionIndex: number, value: string) => void;
  onActionDelete: (dayKey: string, actionIndex: number) => void;
  onShowActionInfo: (value: string) => void;
  onDeleteDay: (dayKey: string) => void;
  isUpdateAuto: boolean;
  statusData?: Array<StatusData>;
  userListData?: Array<UserData>;
  labelData?: Array<LabelData>;
  leadListData?: Array<LeadListData>;
  whatsappTemplate?: Array<TemplateData>;
  emailTemplate?: Array<TemplateData>;
  taskData?: Array<TaskData>;
  errors?: {
    actions?: string;
  };
  isDelete?: boolean;
  metrics?: DayMetrics;
}

const DayGroup: React.FC<DayGroupProps> = ({
  dayKey,
  actions,
  onAddAction,
  onActionChange,
  onActionDelete,
  onShowActionInfo,
  onDeleteDay,
  isUpdateAuto,
  statusData = [],
  userListData = [],
  labelData = [],
  leadListData = [],
  whatsappTemplate = [],
  emailTemplate = [],
  taskData = [],
  errors,
  isDelete,
  metrics, 
}) => {
  const [currentDay, setCurrentDay] = useState(dayKey);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const dayNumber = parseInt(dayKey.split("_")[1]);
  const [type, setType] = useState("");

  // NEW: State for action selection popup
  const [showActionPopup, setShowActionPopup] = useState<boolean>(false);
  const [currentActionIndex, setCurrentActionIndex] = useState<number>(-1);

  useEffect(() => {
    if (type) {
      setTimeout(() => {
        onShowActionInfo(type);
      }, 500);
    }
  }, [type]);

  // NEW: Handle action selection from popup - calls existing functions exactly the same
  const handleActionSelect = (actionIndex: number, value: string) => {
    // Call existing functions with same parameters
    onActionChange(dayKey, actionIndex, value);
    setType(value);
    setShowActionPopup(false);
  };

  // NEW: Open action popup for specific action
  const openActionPopup = (actionIndex: number) => {
    setCurrentActionIndex(actionIndex);
    setShowActionPopup(true);
  };

  const getActionDisplayName = (action: ActionData) => {
    const actionType = automationActionData.find(
      (d) => d.value === action.type
    );

    const baseName = actionType?.name || action.type;

    if (!action.data || Object.keys(action.data).length === 0) {
      return baseName;
    }

    switch (action.type) {
case "push_notification":
  if (action.data.push_notification) {
    const user = userListData.find(
      (u) => u._id === action.data.push_notification
    );
    return user ? `${baseName} - ${user.firstName}` : baseName;
  }
  break;

        case "send_email":
          case "send_gmail":
          case "send_zeptomail":
        if (action?.data?.content) {
          const template: any = emailTemplate.find(
            (t) => t._id === action.data.content
          );

          let displayName = template
            ? `${baseName} - ${template?.details?.title}`
            : baseName;

          // Show sender email if available
          if (action.data.senderDetail?.email) {
            displayName += ` (${action.data.senderDetail.email})`;
          }

          return displayName;
        }
        break;

      case "send_whatsapp":
        if (action.data.template) {
          const template = whatsappTemplate.find(
            (t) => t._id === action.data.template
          );
          return template ? `${baseName} - ${template.name}` : baseName;
        }
        break;
case "add_facebook_audience":
  if (action.data.adAccountId && action.data.audienceName) {
    return `${baseName} - ${action.data.audienceName}`;
  }
  break;
  
         case "add_to_google_sheet":
      return `${baseName} - Auto-create Sheet`;

      case "status_change":
        if (action.data.status) {
          const status = statusData.find((s) => s.value === action.data.status);
          return status ? `${baseName} - ${status.name}` : baseName;
        }
        break;

      case "assign_lead":
        if (action.data.assignToUser) {
          const user = userListData.find(
            (u) => u._id === action.data.assignToUser
          );
          return user ? `${baseName} - ${user.firstName}` : baseName;
        }
        break;

      case "label_change":
        if (action.data.label) {
          const label = labelData.find((l) => l.value === action.data.label[0]);
          return label ? `${baseName} - ${label.name}` : baseName;
        }
        break;

      case "move_to_list":
      case "copy_to_list":
        if (action.data.targetListId) {
          const list = leadListData.find(
            (l) => l._id === action.data.targetListId
          );
          return list ? `${baseName} - ${list.name}` : baseName;
        }
        break;

      case "create_task":
        if (action.data.type) {
          const task = taskData.find((t) => t.value === action.data.type);
          return task ? `${baseName} - ${task.name}` : baseName;
        }
        break;

      case "webhook":
        if (action.data.webhookUrl) {
          try {
            const url = new URL(action.data.webhookUrl);
            return `${baseName} - ${url.hostname}`;
          } catch {
            return `${baseName} - ${action.data.webhookServiceType || 'Custom'}`;
          }
        }
        break;
    }

    return baseName;
  };

  return (
    <div className="day-section">
      <div className="day-header">
        <div className="day-badge">Day {dayNumber}</div>
          {/* Compact metrics display */}
          <div className="spacer"></div>
  
  {/* Metric cards with Lucide icons */}
  {metrics && metrics.totalActions > 0 && (
    <div className="metric-cards">
        <div className="metric-card total" title="Total Actions">
        <CircleDot size={14} />
        <span className="metric-label">Total</span>
        <span className="metric-value">{metrics.totalActions}</span>
      </div>
      <div className="metric-card success" title="Completed Actions">
        <CheckCircle size={14} />
        <span className="metric-label">Success</span>
        <span className="metric-value">{metrics.completedActionsCount}</span>
      </div>
      <div className="metric-card error" title="Failed Actions">
        <XCircle size={14} />
        <span className="metric-label">Failed</span>
        <span className="metric-value">{metrics.failedActionsCount}</span>
      </div>
    
    </div>
  )}
  
  
        {isDelete && (
          <Trash2
            size={18}
            className="delete-day-button"
            onClick={() => setShowDeleteModal(true)}
          />
        )}
      </div>

      {actions.map((action, index) => (
        <div key={index} className="auto_select_action">
          <label className="action-label">Action {index + 1}:</label>
          <div className="action-row">
            
            {/* REPLACED: Dropdown with button that opens popup */}
            <div 
              className="action-selector-button"
              onClick={() => openActionPopup(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                minHeight: '38px'
              }}
            >
              <span 
                style={{
                  fontSize: '14px',
                  color: action.type ? '#1a202c' : '#9ca3af',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {action.type ? getActionDisplayName(action) : 'Select action'}
              </span>
              <ChevronDown size={16} style={{ color: '#9ca3af', marginLeft: '8px' }} />
            </div>

            <div className="action-buttons">
              {action.type && (
                <Settings2
                  size={18}
                  className="action-button-settings"
                  onClick={() => onShowActionInfo(action.type)}
                />
              )}

              <Trash2
                size={18}
                className="action-button-delete"
                onClick={() => onActionDelete(dayKey, index)}
              />
            </div>
          </div>
        </div>
      ))}

      {errors?.actions && <div className="error-message">{errors.actions}</div>}

      <div className="add-action-button" onClick={() => onAddAction(dayKey)}>
        <Plus size={18} />
        <span>Add Action</span>
      </div>

      {/* NEW: Action Selection Popup */}
      <ActionSelectionPopup
        isOpen={showActionPopup}
        onClose={() => setShowActionPopup(false)}
        onSelect={(value) => handleActionSelect(currentActionIndex, value)}
        currentValue={currentActionIndex >= 0 ? actions[currentActionIndex]?.type || '' : ''}
        actionData={automationActionData} // Use existing data source
      />

      <ConfirmationModal
        showModal={showDeleteModal}
        toggleModal={() => setShowDeleteModal(false)}
        onConfirmation={() => {
          onDeleteDay(dayKey);
          setShowDeleteModal(false);
        }}
        message="Are you sure you want to delete this day?"
        title="Remove Day"
      />
    </div>
  );
};

export default DayGroup;